/**
 * The invoice file beside the import review (TASK-0232): the original PDF or photo the lines were
 * read from, so the person checking the lines can look at the paper without leaving the screen.
 *
 * The file is fetched through the API client (cookie auth, one refresh on expiry) as a Blob and
 * shown from an object URL, so a missing or unreadable file is a plain sentence here rather than a
 * raw error page inside a frame. The API serves it from `GET /ai/invoice-imports/:id/file`.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, FileWarning } from 'lucide-react';
import { api, errorMessage } from '@/lib/api/client';
import { Button } from '@/ui/Button';
import { Skeleton } from '@/ui/Panel';
import { cn } from '@/lib/cn';
import { smartDate } from '@/lib/format';
import { importKeys } from './api';
import type { InvoiceFileKind } from './types';

export function InvoiceFilePane({
  importId,
  fileKind,
  filename,
  uploadedBy,
  uploadedAt,
  onHide,
  className,
}: {
  importId: number;
  fileKind: InvoiceFileKind;
  filename: string | null;
  uploadedBy: string | null;
  uploadedAt: string | null;
  onHide: () => void;
  className?: string;
}) {
  const file = useQuery({
    queryKey: importKeys.file(importId),
    queryFn: () =>
      api.get<Blob>(`/ai/invoice-imports/${importId}/file`, {
        raw: true,
        timeout: 120_000,
      }),
    // The stored upload never changes; keep it only while the review is open.
    staleTime: Infinity,
    gcTime: 60_000,
    retry: false,
  });

  const [url, setUrl] = useState<string | null>(null);
  const blob = file.data ?? null;
  useEffect(() => {
    if (!blob) {
      setUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob]);

  const [imageFailed, setImageFailed] = useState(false);

  const who = [
    uploadedBy ? `Uploaded by ${uploadedBy}` : null,
    uploadedAt ? smartDate(uploadedAt) : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const shown = fileKind === 'pdf' || (fileKind === 'image' && !imageFailed);

  return (
    <aside
      aria-label="Invoice file"
      className={cn(
        'border-line bg-surface flex min-h-0 flex-col overflow-hidden rounded border',
        className
      )}
    >
      <header className="border-line flex shrink-0 items-start gap-2 border-b px-3 py-2">
        <div className="min-w-0 flex-1">
          <div className="text-ink truncate text-xs font-medium">
            {filename ?? 'Invoice file'}
          </div>
          {who ? <div className="text-2xs text-ink-faint truncate">{who}</div> : null}
        </div>
        {url ? (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="text-2xs text-signal-ink flex shrink-0 items-center gap-1 hover:underline"
          >
            Open <ExternalLink className="size-3" />
          </a>
        ) : null}
        <Button size="xs" variant="ghost" onClick={onHide}>
          Hide invoice file
        </Button>
      </header>

      <div className="bg-surface-sunken relative min-h-0 flex-1 overflow-auto">
        {file.isPending ? (
          <div className="space-y-2 p-3">
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : file.isError ? (
          <FileProblem title="The original file could not be shown">
            {errorMessage(file.error)}
          </FileProblem>
        ) : url && shown && fileKind === 'pdf' ? (
          <iframe
            title="Invoice file"
            src={url}
            className="block h-full min-h-[24rem] w-full border-0"
          />
        ) : url && shown ? (
          <img
            src={url}
            alt="Invoice file"
            onError={() => setImageFailed(true)}
            className="block w-full"
          />
        ) : url ? (
          <FileProblem title="This kind of file cannot be shown here">
            <a
              href={url}
              download={filename ?? `invoice-${importId}`}
              className="text-signal-ink hover:underline"
            >
              Download it to look at it
            </a>
          </FileProblem>
        ) : null}
      </div>
    </aside>
  );
}

function FileProblem({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-1.5 p-6 text-center">
      <FileWarning className="text-ink-faint size-5" />
      <div className="text-ink text-xs font-medium">{title}</div>
      <div className="text-2xs text-ink-muted">{children}</div>
    </div>
  );
}
